import { useMemo, type ReactNode } from 'react';
import { SystemSettingsContext } from './SystemSettingsContext';
import { useConfigLoader } from './ConfigLoaderContext';

export interface SystemSettingsProviderProps {
  children: ReactNode;
}

/**
 * System Settings Provider Component
 * Provides system settings from the unified config loader
 *
 * NOTE: All loading and application of settings is handled by
 * useUnifiedConfigLoader (called in SplashScreen via ConfigLoaderProvider).
 * This provider simply exposes those settings to the component tree.
 */
export const SystemSettingsProvider = ({ children }: SystemSettingsProviderProps) => {
  // Get settings from the unified config loader
  const { settings, isLoading, error } = useConfigLoader();

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    settings,
    isLoading,
    error,
    // refreshSettings not needed - handled by config loader
    refreshSettings: async () => {
      console.warn('refreshSettings called but config is managed by unified loader');
    },
  }), [settings, isLoading, error]);

  return (
    <SystemSettingsContext.Provider value={contextValue}>
      {children}
    </SystemSettingsContext.Provider>
  );
};
